import cn from "classnames";
import React, { useEffect, useRef } from "react";
import { Resizable } from "react-resizable";

import { useIsFirstUser } from "hooks/useIsFirstUser";
import { useDatabasesNames } from "services/databases/useDatabasesNames";
import { useWorkspaceEngines } from "services/engines/useWorkspaceEngines";

import DocumentEditor from "pages/DevelopWorkspace/Editor/Document/DocumentContent/DocumentEditor/DocumentEditor";
import useActiveEditorView from "pages/DevelopWorkspace/contexts/ActiveEditorViewContext/hooks/useActiveEditorView";
import { useDocuments } from "pages/DevelopWorkspace/contexts/DocumentsContext/hooks/useDocuments";
import { useScripts } from "pages/DevelopWorkspace/contexts/ScriptsContext/ScriptsContext";
import { getDocumentScript } from "pages/DevelopWorkspace/helpers/getDocumentScript";
import { WorkspaceDocument } from "pages/DevelopWorkspace/workspace.types";

import { useLayoutToggles } from "components/LayoutToggles/context";

import { useAutoSetContext } from "./useAutoSetContext";

import styles from "./DocumentContent.module.scss";

interface DocumentContentProps {
  document: WorkspaceDocument;
  runText: (
    content: string,
    selectionOffset: number,
    lineNumber: number
  ) => void;
}

const HEADER_HEIGHT_PX = 49;
const FOOTER_HEIGHT_PX = 49;
const MIN_HEIGHT_PX = 150;
const DEFAULT_MAX_HEIGHT_PX = 600;
const MAX_HEIGHT_MARGIN_PX = 144;

const adjustEditorHeight = (height?: number) => {
  // if height is not provided, use the 100%

  const editor = window.document.querySelector(".cm-editor");
  if (!editor) {
    return;
  }

  if (height) {
    editor.setAttribute("style", `height: ${height}px`);
  } else {
    editor.setAttribute("style", `height: 100%`);
  }
};

const DocumentContent = (props: DocumentContentProps) => {
  const { document, runText } = props;
  const { state: scriptsState } = useScripts();
  const {
    actions: { updateDocumentContent, changeDocumentLayout },
  } = useDocuments();
  const { setActiveEditorView } = useActiveEditorView();
  const isFirstUser = useIsFirstUser();

  const { data: engines } = useWorkspaceEngines({
    includeSystemEngine: true,
    isFirstUser,
  });

  const databases = useDatabasesNames({
    isFirstUser,
    showSystemDatabases: true,
  });

  const { layout } = useLayoutToggles();
  const editorHeightPx = document.layout.editorHeightPx;
  const rootRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = React.useState(editorHeightPx);
  const [maxHeightConstraint, setMaxHeightConstraint] = React.useState<number>(
    DEFAULT_MAX_HEIGHT_PX
  );

  const expanded = layout.results.expanded;

  const computeMaxAvailableHeight = () => {
    const topPosition = rootRef.current?.getBoundingClientRect().top ?? 0;
    const viewportHeight = window.innerHeight || 0;
    const available = viewportHeight - topPosition - MAX_HEIGHT_MARGIN_PX;
    return Math.max(MIN_HEIGHT_PX, Math.floor(available));
  };

  useEffect(() => {
    setHeight(document.layout.editorHeightPx);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on document ID change
  }, [document.id]);

  useAutoSetContext({
    engines: engines || [],
    databases,
    document,
  });

  const syncEditorHeight = () => {
    if (expanded) {
      adjustEditorHeight(height);
    } else {
      adjustEditorHeight();
    }
  };

  useEffect(() => {
    syncEditorHeight();

    if (expanded) {
      const updateMaxConstraint = () => {
        const newMax = computeMaxAvailableHeight();
        setMaxHeightConstraint(newMax);
        if (height > newMax) {
          setHeight(newMax);
          syncEditorHeight();
        }
      };

      updateMaxConstraint();
      window.addEventListener("resize", updateMaxConstraint);
      return () => {
        window.removeEventListener("resize", updateMaxConstraint);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only need to run when layout.results.expanded changes
  }, [expanded]);

  const script = getDocumentScript(document, scriptsState);

  return (
    <Resizable
      width={Infinity}
      onResize={(event, { size }) => {
        if (height !== size.height) {
          setHeight(size.height);
        }
      }}
      onResizeStop={(event, { size }) => {
        adjustEditorHeight(size.height);

        changeDocumentLayout(document.id, {
          editorHeightPx: size.height,
        });
      }}
      axis="y"
      height={height}
      minConstraints={[Infinity, MIN_HEIGHT_PX]}
      maxConstraints={[Infinity, maxHeightConstraint]}
      handle={
        <div
          className={cn(styles.resizeHandler, {
            [styles.hidden]: !expanded,
          })}
        />
      }
      resizeHandles={["s"]}
    >
      <div
        className={cn(styles.root)}
        ref={rootRef}
        style={{
          height: expanded
            ? `${height}px`
            : `calc(100% - ${HEADER_HEIGHT_PX}px - ${FOOTER_HEIGHT_PX}px)`,
        }}
      >
        <div className={styles.body}>
          {script && (
            <DocumentEditor
              key={`${document.id}-${script.editorState.original !== null ? "merge" : "edit"}`}
              documentId={document.id}
              documentScript={script}
              onEditorStateUpdate={editorState => {
                updateDocumentContent(document.id, editorState);
              }}
              onInit={cm => {
                setActiveEditorView(cm);
                syncEditorHeight();
              }}
              onRun={runText}
            />
          )}
        </div>
      </div>
    </Resizable>
  );
};

export default DocumentContent;
